import { ENTITIES } from './data';
import { GUIDES } from './pages/Guides';

const SITE_URL = 'https://www.revvly.ai';

export type DocsPageKind = 'TechArticle' | 'DefinedTerm' | 'WebPage';

export interface DocsPageMeta {
  title: string;
  description: string;
  kind: DocsPageKind;
  /** Label used in the BreadcrumbList; falls back to `title` when omitted. */
  breadcrumbLabel?: string;
}

/** Static top-level pages that aren't driven by the Guides/API/Concept data below. */
const STATIC_PAGES: Record<string, DocsPageMeta> = {
  '/docs': {
    title: 'Revvly Docs — Subscription Revenue Management Reference',
    description:
      'Reference documentation for Revvly: subscription catalog, pricing engine, billing, CRM, usage ingestion, and the public API.',
    kind: 'WebPage',
    breadcrumbLabel: 'Docs',
  },
  '/docs/quickstart': {
    title: 'Quickstart — Revvly Subscription Management Docs',
    description:
      'Set up your catalog, create a client and subscription, and see your first lifecycle event and MRR movement in Revvly.',
    kind: 'TechArticle',
    breadcrumbLabel: 'Quickstart',
  },
  '/docs/example': {
    title: 'Worked Example — Revvly Subscription Revenue Management Docs',
    description:
      'A single fictional customer followed end to end through Revvly’s catalog, pricing, subscription, and billing model.',
    kind: 'TechArticle',
    breadcrumbLabel: 'Worked example',
  },
  '/docs/guides': {
    title: 'How-To Guides — Revvly Subscription Revenue Management',
    description:
      'Step-by-step guides for the most common Revvly workflows: pricing, billing, renewals, CRM, usage ingestion, and integrations.',
    kind: 'WebPage',
    breadcrumbLabel: 'Guides',
  },
  '/docs/changelog': {
    title: 'Changelog — Revvly Platform Updates',
    description: 'What shipped on the Revvly platform, in reverse chronological order.',
    kind: 'WebPage',
    breadcrumbLabel: 'Changelog',
  },
  '/docs/assistant': {
    title: 'The Revvly Assistant — Revvly Docs',
    description:
      'Ask the in-product Revvly Assistant about clients, subscriptions, invoices, and revenue, and have it draft catalog and subscription changes with a human confirmation step.',
    kind: 'TechArticle',
    breadcrumbLabel: 'Assistant',
  },
  '/docs/api': {
    title: 'API Reference — Revvly Docs',
    description:
      'Every documented Revvly public API endpoint: usage ingestion, invoice payment webhooks, public GraphQL, and discovery reads.',
    kind: 'WebPage',
    breadcrumbLabel: 'API',
  },
  '/docs/api/mcp': {
    title: 'Connect Claude with MCP — Revvly Docs',
    description:
      'Connect Claude Desktop, Claude Code, or any MCP client to live Revvly billing data, with human-approved writes.',
    kind: 'TechArticle',
    breadcrumbLabel: 'MCP connection',
  },
  '/docs/api/events': {
    title: 'Event Ingestion API — Revvly Docs',
    description:
      'Stream metered usage events into Revvly over REST, with HMAC request signing and idempotent delivery.',
    kind: 'TechArticle',
    breadcrumbLabel: 'Event Ingestion API',
  },
  '/docs/api/product-events': {
    title: 'Product Events API — Revvly Docs',
    description:
      'Send product usage events to Revvly from your backend to see how each client uses your product, and erase one user’s events on request.',
    kind: 'TechArticle',
    breadcrumbLabel: 'Product Events API',
  },
  '/docs/api/invoice-payments': {
    title: 'Invoice Payment Webhook — Revvly Docs',
    description:
      'Mark a Revvly invoice paid, in full or in part, the moment your payment processor or bank feed confirms it.',
    kind: 'TechArticle',
    breadcrumbLabel: 'Invoice Payment Webhook',
  },
  '/docs/api/graphql-public': {
    title: 'Public GraphQL API — Revvly Docs',
    description:
      'Read clients, subscriptions, and invoices, and submit prospects, against Revvly’s curated public GraphQL schema.',
    kind: 'TechArticle',
    breadcrumbLabel: 'Public GraphQL API',
  },
  '/docs/api/external-references': {
    title: 'External References — Revvly Docs',
    description:
      'Address Revvly subscriptions and variables by your own external ids instead of Revvly-issued ones.',
    kind: 'TechArticle',
    breadcrumbLabel: 'External references',
  },
};

/** Guide pages (`/docs/guides/*` and a few cross-listed under `/docs/api` or `/docs/assistant`). */
const GUIDE_PAGES: Record<string, DocsPageMeta> = Object.fromEntries(
  GUIDES.filter((g) => g.path).map((g) => [
    g.path as string,
    {
      title: `${g.title} — Revvly Docs`,
      description: g.text,
      kind: 'TechArticle' as const,
      breadcrumbLabel: g.title,
    },
  ]),
);

/** Concept/glossary pages at `/docs/concepts/:id`, driven by the same ENTITIES used in the UI. */
const CONCEPT_PAGES: Record<string, DocsPageMeta> = Object.fromEntries(
  Object.values(ENTITIES).map((e) => [
    `/docs/concepts/${e.id}`,
    {
      title: `${e.name} — Revvly Docs`,
      description: e.oneLiner,
      kind: 'DefinedTerm' as const,
      breadcrumbLabel: e.name,
    },
  ]),
);

const DOCS_SEO: Record<string, DocsPageMeta> = {
  ...STATIC_PAGES,
  ...GUIDE_PAGES,
  ...CONCEPT_PAGES,
};

/** Looks up SEO metadata for a docs route. `/docs` and `/docs/` both resolve to the Overview page. */
export function getDocsPageMeta(pathname: string): DocsPageMeta | undefined {
  const normalized = pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  return DOCS_SEO[normalized === '/docs' ? '/docs' : normalized] ?? DOCS_SEO[normalized];
}

/**
 * JSON-LD for a docs page: a `TechArticle` or `DefinedTerm` (schema.org's type
 * for a glossary-style term definition, used for the concept pages) plus a
 * `BreadcrumbList`. This is what makes individual docs pages legible to AI
 * crawlers and search engines as discrete, citable units, not just HTML.
 */
export function buildDocsJsonLd(meta: DocsPageMeta, pathname: string) {
  const url = `${SITE_URL}${pathname}`;
  const breadcrumb = {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: 'Docs', item: `${SITE_URL}/docs` },
      { '@type': 'ListItem', position: 3, name: meta.breadcrumbLabel ?? meta.title, item: url },
    ],
  };

  const publisher = {
    '@type': 'Organization',
    name: 'Revvly',
    url: SITE_URL,
    logo: `${SITE_URL}/favicon.svg`,
  };

  if (meta.kind === 'DefinedTerm') {
    return {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'DefinedTerm',
          name: meta.breadcrumbLabel ?? meta.title,
          description: meta.description,
          url,
          inDefinedTermSet: {
            '@type': 'DefinedTermSet',
            name: 'Revvly subscription billing glossary',
            url: `${SITE_URL}/docs/guides`,
          },
        },
        breadcrumb,
      ],
    };
  }

  if (meta.kind === 'TechArticle') {
    return {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'TechArticle',
          headline: meta.breadcrumbLabel ?? meta.title,
          description: meta.description,
          url,
          mainEntityOfPage: { '@type': 'WebPage', '@id': url },
          publisher,
        },
        breadcrumb,
      ],
    };
  }

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        name: meta.breadcrumbLabel ?? meta.title,
        description: meta.description,
        url,
        publisher,
      },
      breadcrumb,
    ],
  };
}
